// The dispatcher's maps (docs/spec/transport.md slice 8): every route along its roads, every stop
// and school, and every bus live, on the Today tab's overview and the Bus map tab with a list of
// the buses and each one's details. Polls the live view every 10 s.
import { useEffect, useMemo, useState } from "react";
import { Badge, Button } from "@vavr/ui";
import { MapView, time, type MapLine, type MapMarker } from "@vavr/screens/shared";
import { lngLat, runLines, type RunMapStop } from "./runmap.tsx";
import { useGet } from "@vavr/screens/console";

export interface FleetRoute {
  id: string; name: string; kind: "am" | "pm" | "trip"; vehicle: string | null; replaces?: string | null; assigned_driver: string | null; stops: number; riders: number; first_time: string | null; late_start: boolean;
  points: { n: number; name: string; lat: number; lng: number; school: boolean }[];
  run: null | {
    started_at: number; finished_at: number | null; driver: string | null; on_board: number; off: number; absent: number; held: number; waiting: number;
    reached: string | null; reached_index: number; late_min: number | null; next: { name: string; eta_at: number | null } | null;
    id?: string; seen_at: number | null; near: string | null; accuracy: number | null; speed?: number | null; heading?: number | null; distance_mi?: number; bus: { lat: number; lng: number } | null;
  };
  exceptions?: { at: number; child: string; stop: string; what: "absent" | "held" | "restriction" | "unauthorised" }[];
}
export interface Shape { route_id: string; shape: [number, number][]; distance_mi: number | null; straight: boolean }

/** A run's record (GET …/transport/runs/:id): the run, its stops with actual times, its timeline and trail. */
export interface RunRecord {
  run: { id: string; route_id: string; route: string; kind: string; date: string; vehicle: string | null; driver: string | null; started_at: number; finished_at: number | null; distance_mi: number; tz: string; boarded: number; dropped: number; absent: number; held: number };
  stops: { id: string; seq: number; name: string; time: string | null; site_id: string | null; lat: number | null; lng: number | null; planned_at: number | null; arrived_at: number | null; left_at: number | null; late_min: number | null }[];
  timeline: { at: number; type: string; child: string; stop: string | null; adult: string | null; flags: string[] }[];
  trail: { at: number; lat: number; lng: number; speed: number | null; heading: number | null }[];
}

/** The chosen run's record, refreshed with the live view while it is on the road. */
export function useRunRecord(base: string, runId: string | null | undefined, live: boolean) {
  const rec = useGet<RunRecord>(runId ? `${base}/runs/${runId}` : null);
  useEffect(() => { if (!runId || !live) return; const i = setInterval(() => rec.reload(), 10_000); return () => clearInterval(i); }, [runId, live, rec.reload]);
  return runId ? rec.data : null;
}

/** Each stop's state on a run: done once something happened there, the first one after that next. */
export function stopStates(rec: RunRecord): RunMapStop[] {
  const stops = [...rec.stops].sort((a, b) => a.seq - b.seq);
  const lastDone = stops.reduce((i, x, k) => (x.arrived_at != null ? k : i), -1);
  return stops.map((x, k) => ({
    key: x.id, name: x.name, lat: x.lat, lng: x.lng, school: !!x.site_id, n: k + 1,
    state: x.arrived_at != null ? "done" : !rec.run.finished_at && k === lastDone + 1 ? "next" : "todo",
  }));
}

/** "15:05" as people read it: "3:05 PM". */
const clock = (hhmm: string) => { const [h, m] = hhmm.split(":").map(Number) as [number, number]; return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };
const PALETTE = ["#2e9c8e", "#e9a21b", "#6366f1", "#db2777", "#0891b2", "#65a30d", "#dc2626", "#7c3aed"];
const STALE_MS = 2 * 60_000;
export const EXCEPTION: Record<string, string> = { absent: "not at the stop", held: "kept on the bus", restriction: "restriction at the handover", unauthorised: "handed to an adult not on the list" };

/** "Route 3 afternoon" → "R3", "Pittsgrove afternoon" → "PA": short enough for a bus marker. */
export function shortName(name: string) {
  const words = name.split(/\s+/).filter(Boolean);
  const num = words.find((w) => /\d/.test(w));
  return num ? `${words[0]![0]!.toUpperCase()}${num.replace(/\D/g, "")}`.slice(0, 4) : words.slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");
}

export type BusStatus = "waiting" | "late-start" | "on-road" | "late" | "stale" | "alert" | "finished";
export function busStatus(r: FleetRoute, now: number): { status: BusStatus; text: string; tone: "neutral" | "info" | "success" | "warning" | "danger" } {
  const run = r.run;
  if (!run) return r.late_start ? { status: "late-start", text: "Not started, late", tone: "danger" } : { status: "waiting", text: r.first_time ? `Starts ${clock(r.first_time)}` : "Not started", tone: "neutral" };
  if (run.finished_at) return { status: "finished", text: "Finished", tone: "success" };
  if ((r.exceptions ?? []).some((x) => x.what !== "absent") || run.held > 0) return { status: "alert", text: "Needs attention", tone: "danger" };
  if (!run.seen_at || now - run.seen_at > STALE_MS) return { status: "stale", text: run.seen_at ? "No recent location" : "No location yet", tone: "warning" };
  if (run.late_min != null && run.late_min >= 5) return { status: "late", text: `${run.late_min} min late`, tone: "warning" };
  return { status: "on-road", text: "On the road", tone: "info" };
}

export function useFleet(base: string) {
  const live = useGet<{ routes: FleetRoute[]; tz: string }>(`${base}/live`);
  const shapes = useGet<{ shapes: Shape[] }>(`${base}/shapes`);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => { live.reload(); setNow(Date.now()); }, 10_000); return () => clearInterval(i); }, [live.reload]);
  return { live, shapes: shapes.data?.shapes ?? [], now };
}

const ago = (ts: number, now: number) => { const s = Math.round((now - ts) / 1000); return s < 60 ? `${Math.max(0, s)} s ago` : `${Math.round(s / 60)} min ago`; };
const mph = (ms: number | null | undefined) => (ms == null ? null : Math.round(ms * 2.23694));

/** The numbers that matter at a glance. */
export function FleetSummary({ routes, now }: { routes: FleetRoute[]; now: number }) {
  const running = routes.filter((r) => r.run && !r.run.finished_at);
  const st = routes.map((r) => busStatus(r, now).status);
  const facts: [string, number, string?][] = [
    ["Routes today", routes.length],
    ["On the road", running.length],
    ["Children on board", running.reduce((n, r) => n + r.run!.on_board, 0)],
    ["Still to board", running.reduce((n, r) => n + r.run!.waiting, 0)],
    ["Late", st.filter((x) => x === "late" || x === "late-start").length, "warning"],
    ["No recent location", st.filter((x) => x === "stale").length, "warning"],
    ["Exceptions", routes.reduce((n, r) => n + (r.exceptions ?? []).length, 0), "danger"],
    ["Finished", st.filter((x) => x === "finished").length],
  ];
  return (
    <dl className="pmt-fleetsum" aria-label="Today at a glance">
      {facts.map(([label, n, tone]) => <div key={label} data-tone={n > 0 ? tone : undefined}><dt>{label}</dt><dd>{n}</dd></div>)}
    </dl>
  );
}

/** Every route in its colour along its roads, the schools, the selected route's stops, and every running bus. */
export function FleetMap({ routes, shapes, now, selected, onSelect, height, label, record }: {
  routes: FleetRoute[]; shapes: Shape[]; now: number; selected: string | null; onSelect: (id: string | null) => void; height: number | string; label: string;
  /** The chosen run's record: its trail and each stop done or next. */
  record?: RunRecord | null;
}) {
  const color = (id: string) => PALETTE[Math.max(0, routes.findIndex((r) => r.id === id)) % PALETTE.length]!;
  const rec = record && record.run.route_id === selected ? record : null;
  const lines: MapLine[] = useMemo(() => [
    // Other routes in their colours (quieter while one is chosen); the chosen run: planned dashed, driven solid.
    ...shapes.filter((s) => routes.some((r) => r.id === s.route_id) && !(rec && s.route_id === selected)).map((s) => ({
      id: s.route_id, coords: s.shape, color: color(s.route_id), width: s.route_id === selected ? 7 : 4, opacity: selected && s.route_id !== selected ? 0.35 : 0.9,
    })),
    ...(rec ? runLines(rec.run.id, shapes.find((s) => s.route_id === selected)?.shape, lngLat(rec.trail)) : []),
  ], [shapes, routes, selected, rec]);
  const schools = new Map<string, MapMarker>();
  for (const r of routes) for (const p of r.points.filter((x) => x.school)) schools.set(`${p.lat.toFixed(5)},${p.lng.toFixed(5)}`, { id: `school-${p.lat}-${p.lng}`, lat: p.lat, lng: p.lng, kind: "school", title: p.name });
  const sel = routes.find((r) => r.id === selected);
  const markers: MapMarker[] = [
    ...schools.values(),
    ...(rec ? stopStates(rec).filter((x) => !x.school && x.lat != null && x.lng != null).map((x): MapMarker => ({ id: `stop-${sel!.id}-${x.n}`, lat: x.lat!, lng: x.lng!, kind: "stop", label: String(x.n), title: `${x.n}. ${x.name}`, tone: x.state === "done" ? "done" : x.state === "next" ? "next" : undefined }))
      : sel ? sel.points.filter((p) => !p.school).map((p): MapMarker => ({ id: `stop-${sel.id}-${p.n}`, lat: p.lat, lng: p.lng, kind: "stop", label: String(p.n), title: `${p.n}. ${p.name}` })) : []),
    ...routes.filter((r) => r.run?.bus && !r.run.finished_at).map((r): MapMarker => {
      const s = busStatus(r, now);
      return {
        id: `bus-${r.id}`, lat: r.run!.bus!.lat, lng: r.run!.bus!.lng, kind: "bus", label: shortName(r.name), heading: r.run!.heading ?? null,
        title: `${r.name}: ${s.text}${r.run!.near ? `, near ${r.run!.near}` : ""}`, onClick: () => onSelect(r.id === selected ? null : r.id),
        tone: r.id === selected ? "selected" : s.status === "late" ? "late" : s.status === "alert" ? "alert" : s.status === "stale" ? "stale" : undefined,
      };
    }),
  ];
  // First view: the buses on the road and their routes' stops; with none running, every route.
  const running = routes.filter((r) => r.run && !r.run.finished_at);
  const focus = running.length ? running : routes;
  const fitTo = [...focus.map((r) => `bus-${r.id}`), ...focus.flatMap((r) => r.points.filter((p) => p.school).map((p) => `school-${p.lat}-${p.lng}`))];
  return <MapView label={label} height={height} markers={markers} lines={lines} fitTo={fitTo} follow={selected ? [`bus-${selected}`] : undefined} onPick={() => onSelect(null)} />;
}

/** One bus: everything the dispatcher knows about it now. */
export function BusDetail({ r, tz, now, onClose }: { r: FleetRoute; tz: string; now: number; onClose?: () => void }) {
  const s = busStatus(r, now);
  const run = r.run;
  const speed = mph(run?.speed);
  return (
    <section className="pmt-busdetail" aria-label={`${r.name} details`}>
      <header className="pmt-busdetail__head">
        <div>
          <h3 className="pmt-card__title">{r.name}</h3>
          <p className="pmu-small pmu-muted">{r.kind === "am" ? "To school" : r.kind === "pm" ? "From school" : "Trip"} · {busText(r)} · {run?.driver ?? r.assigned_driver ?? "No driver assigned"}</p>
        </div>
        <span className="pmu-row"><Badge tone={s.tone}>{s.text}</Badge>{onClose && <Button size="sm" variant="ghost" icon="close" aria-label="Close" onClick={onClose} />}</span>
      </header>
      {run && (
        <div className="pmt-progress" role="img" aria-label={`Stop ${run.reached_index + 1} of ${r.stops}`}>
          {Array.from({ length: r.stops }, (_, i) => <span key={i} className="pmt-progress__stop" data-done={i <= run.reached_index ? "" : undefined} />)}
        </div>
      )}
      <dl className="pmt-livefacts">
        <div><dt>Children</dt><dd>{r.riders}</dd></div>
        {run && <div><dt>On board</dt><dd>{run.on_board}</dd></div>}
        {run && <div><dt>Off</dt><dd>{run.off}</dd></div>}
        {run && <div><dt>Still to board</dt><dd>{run.waiting}</dd></div>}
        {run && run.absent > 0 && <div><dt>Not at the stop</dt><dd>{run.absent}</dd></div>}
        {run && run.held > 0 && <div><dt>Kept on the bus</dt><dd>{run.held}</dd></div>}
        {!run && <div><dt>First stop</dt><dd>{r.first_time ? clock(r.first_time) : "No time set"}</dd></div>}
        {run && <div><dt>Started</dt><dd>{time(run.started_at, tz)}</dd></div>}
        {run?.finished_at && <div><dt>Finished</dt><dd>{time(run.finished_at, tz)}</dd></div>}
        {run && !run.finished_at && <div><dt>Last stop</dt><dd>{run.reached ?? "Not yet"}</dd></div>}
        {run && !run.finished_at && <div><dt>Next stop</dt><dd>{run.next ? `${run.next.name}${run.next.eta_at ? `, ${time(run.next.eta_at, tz)}` : ""}` : "School"}</dd></div>}
        {run && !run.finished_at && run.late_min != null && <div><dt>Against the plan</dt><dd>{run.late_min > 0 ? `${run.late_min} min late` : run.late_min < 0 ? `${-run.late_min} min early` : "On time"}</dd></div>}
        {run && !run.finished_at && <div><dt>Last seen</dt><dd>{run.seen_at ? `${ago(run.seen_at, now)}${run.near ? `, near ${run.near}` : ""}` : "No location"}</dd></div>}
        {run && !run.finished_at && speed != null && <div><dt>Speed</dt><dd>{speed} mph</dd></div>}
        {run && !run.finished_at && run.accuracy != null && <div><dt>GPS accuracy</dt><dd>{Math.round(run.accuracy * 3.28084)} ft</dd></div>}
        {run?.distance_mi != null && <div><dt>Driven</dt><dd>{run.distance_mi} mi</dd></div>}
      </dl>
      {(r.exceptions ?? []).length > 0 && (
        <ul className="pmt-exceptions" aria-label="Exceptions">
          {r.exceptions!.map((x, i) => <li key={i}><Badge tone={x.what === "absent" ? "warning" : "danger"}>{time(x.at, tz)}</Badge> {x.child}: {EXCEPTION[x.what]}{x.stop ? `, ${x.stop}` : ""}</li>)}
        </ul>
      )}
    </section>
  );
}

/** The Bus map tab: a big live map and the list of buses; choosing one follows it and shows its details. */
export function BusMap({ base, tzDefault }: { base: string; tzDefault: string }) {
  const { live, shapes, now } = useFleet(base);
  const [selected, setSelected] = useState<string | null>(null);
  const routes = live.data?.routes ?? [];
  const tz = live.data?.tz ?? tzDefault;
  const order: BusStatus[] = ["alert", "late-start", "late", "stale", "on-road", "waiting", "finished"];
  const sorted = [...routes].sort((a, b) => order.indexOf(busStatus(a, now).status) - order.indexOf(busStatus(b, now).status));
  const sel = routes.find((r) => r.id === selected);
  const record = useRunRecord(base, sel?.run?.id, !!sel?.run && !sel.run.finished_at);
  if (live.loading && !live.data) return <p className="pmu-muted">Loading the buses…</p>;
  if (!routes.length) return <p className="pmu-muted">No route runs today.</p>;
  return (
    <div className="pmu-stack">
      <FleetSummary routes={routes} now={now} />
      <div className="pmt-busmap">
        <div className="pmt-busmap__map"><FleetMap routes={routes} shapes={shapes} now={now} selected={selected} onSelect={setSelected} height="100%" label="All buses on the map" record={record} /></div>
        <aside className="pmt-busmap__side" aria-label="Buses">
          {sel ? <BusDetail r={sel} tz={tz} now={now} onClose={() => setSelected(null)} /> : <p className="pmu-small pmu-muted">Choose a bus on the map or in the list to follow it. Updates every 10 seconds.</p>}
          <ul className="pmt-buslist">
            {sorted.map((r) => {
              const s = busStatus(r, now);
              return (
                <li key={r.id}>
                  <button type="button" className="pmt-buslist__row" aria-pressed={r.id === selected} onClick={() => setSelected(r.id === selected ? null : r.id)}>
                    <span className="pmt-buslist__tag" data-status={s.status}>{shortName(r.name)}</span>
                    <span className="pmt-buslist__main">
                      <b>{r.name}</b>
                      <span className="pmu-small pmu-muted">{busText(r)} · {r.run?.driver ?? r.assigned_driver ?? "No driver"}{r.run && !r.run.finished_at ? ` · ${r.run.on_board} on board` : ""}</span>
                      {r.run && !r.run.finished_at && <span className="pmu-small">{r.run.next ? `Next: ${r.run.next.name}${r.run.next.eta_at ? `, ${time(r.run.next.eta_at, tz)}` : ""}` : "Heading to school"}</span>}
                    </span>
                    <Badge tone={s.tone}>{s.text}</Badge>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>
      </div>
    </div>
  );
}

/** The bus a route runs with, and the usual one when another stands in today (PMU-232). */
export const busText = (r: { vehicle: string | null; replaces?: string | null }) => (r.vehicle ? `${r.vehicle}${r.replaces ? ` (for ${r.replaces})` : ""}` : "No vehicle");
